// Where a lesson lands when it is dropped on the web calendar (#571).
//
// Two drops, one per kind of grid:
//
//   * **Week · Agenda, and the Day view** (the same time grid, one column
//     wide) — the day *and* the time under the pointer, the time in steps of
//     `DROP_STEP_MINUTES`.
//   * **Month** — the day only. The time is not under the pointer on a month
//     cell, so the drop keeps the entry's own time and the calendar then opens
//     a window to change it (CalendarPage.jsx).
//
// What a drop *writes* is not decided here: it is `reschedulePatch` in
// domain/eventSeries.js, the core's `reschedule_patch`, which the resize drag
// and the phones' Reschedule also write (#482) — that one occurrence only,
// a bare override, no scope question and no conflict check. This module only
// turns a pointer into the `{ date, time }` that rule is handed.
//
// ── Time zones: none ───────────────────────────────────────────────────────
//
// A lesson's `startTime` is wall-clock time in its **series' own zone**
// (domain/timeZone.js), and the grid draws it exactly as stored — 16:00 is
// drawn at four o'clock whatever zone the studio or the browser is in. So the
// time under the pointer is already in the frame the override is read in, and
// converting it through the studio's zone would land a lesson from a series
// made elsewhere an offset away from where it was let go. Neither the studio's
// zone nor the browser's enters here, and nothing here builds a `Date`: the
// column's date is a date-only string, the next day is `addDays` on the UTC
// grid (CLAUDE.md — a date built in local time lands a day early west of
// Greenwich), and a time is minutes since midnight written back as `HH:MM`.
//
// ── Not in the core ────────────────────────────────────────────────────────
//
// The phones have no grid to drop on — their answer to the drag is the
// Reschedule form (mobile/WONKY.md), which already writes through the core.
// So this is presentation geometry with one platform, and lives beside the
// rest of the web's calendar rules rather than behind the FFI.

import { addDays } from './recurrence.js';

export const DROP_STEP_MINUTES = 5;
export const DAY_MINUTES = 24 * 60;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const pad = (n) => String(n).padStart(2, '0');

// Minutes since midnight as the `HH:MM` every client stores.
export function clockFromMinutes(minutes) {
  const m = Math.max(0, Math.min(DAY_MINUTES - 1, Math.round(minutes)));
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
}

// `HH:MM` as minutes since midnight, or null for anything that is not one.
export function minutesFromClock(clock) {
  const m = /^(\d{2}):(\d{2})$/.exec(String(clock ?? ''));
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

// The minute of the day under a point `offsetPx` below the top of a grid
// column that starts at `gridStartMinute` and draws `pixelsPerMinute`. Not
// snapped: the snap is the drop's, after the grab offset is taken off.
export function minuteAt(offsetPx, pixelsPerMinute, gridStartMinute = 0) {
  if (!(pixelsPerMinute > 0)) return gridStartMinute;
  return gridStartMinute + offsetPx / pixelsPerMinute;
}

// Which column a horizontal position is over, given each column's left and
// right edge in the same coordinates. A point left of the first column or
// right of the last is taken as the nearest one — a lesson let go a few pixels
// past the grid's edge was still being dragged onto that edge's day — and a
// point in a gap between two columns as the nearer of the two.
export function columnAt(x, columns) {
  if (!columns?.length) return -1;
  let best = 0;
  let bestDistance = Infinity;
  columns.forEach((c, i) => {
    if (!c) return;
    const distance = x < c.left ? c.left - x : x > c.right ? x - c.right : 0;
    if (distance < bestDistance) {
      best = i;
      bestDistance = distance;
    }
  });
  return best;
}

// The week grid's drop: where a lesson starts when it is let go.
//
//   firstDate      the first column's date, `YYYY-MM-DD`
//   column         the column under the pointer (0 for the Day view's one)
//   pointerMinute  the minute of the day under the pointer (`minuteAt`)
//   grabMinute     how far into the lesson it was picked up, in minutes — so
//                  a lesson picked up by its middle is let go by its middle,
//                  and does not jump to put its top edge under the pointer
//   durationMin    its length, which keeps it on the grid at the bottom
//   gridStart, gridEnd   the minutes of the day the grid draws
//
// The start is the nearest `step` — rounded, not floored, so a pointer let go
// at 4:03 gives 4:05 rather than the 4:00 it has already passed the middle of —
// and is then held on the grid: no earlier than its first drawn step and no
// later than the last one the whole lesson still fits under. The web's grid
// is the whole day (#749), so its bounds are midnight and the last start
// that ends by the next midnight — an occurrence is one date, and a lesson let
// go past that would run into a day it is not on.
//
// Null when the column's date is not a date-only string — the caller writes
// nothing rather than a move to somewhere unparseable.
export function gridDrop({
  firstDate, column = 0, pointerMinute, grabMinute = 0, durationMin,
  gridStart = 0, gridEnd = DAY_MINUTES, step = DROP_STEP_MINUTES,
}) {
  if (!DATE_ONLY.test(String(firstDate ?? ''))) return null;
  if (!Number.isFinite(pointerMinute)) return null;
  const length = Number.isFinite(durationMin) && durationMin > 0 ? durationMin : step;
  const lo = Math.ceil(Math.max(0, gridStart) / step) * step;
  const hi = Math.max(lo, Math.floor((Math.min(DAY_MINUTES, gridEnd) - length) / step) * step);
  const snapped = Math.round((pointerMinute - (grabMinute || 0)) / step) * step;
  const start = Math.min(hi, Math.max(lo, snapped));
  return {
    date: column ? addDays(firstDate, column) : firstDate,
    time: clockFromMinutes(start),
  };
}

// The month grid's drop: the day of the cell it was let go on, or null when
// that is the day it is already on — a chip picked up and put back is not a
// move, and must not open the time window as if it were.
export function dayDrop(fromDate, toDate) {
  if (!DATE_ONLY.test(String(toDate ?? ''))) return null;
  return toDate === fromDate ? null : toDate;
}
